import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Map as MapIcon, List } from 'lucide-react';
import LocationModal from './components/LocationModal';
import { DISNEY_CALIFORNIA_ADVENTURE_WAIT_TIMES_API } from './config'
import ParkInteractiveMap from './components/ParkInteractiveMap';
import useEntityMapView from './hooks/useEntityMapView';
import { RestaurantsSkeleton } from './components/skeletons';

// Unused functions - reserved for future implementation
// const formatTime = (time) => {
//   if (!time) return '';
//   return new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// };

// const getRestaurantStatus = (openingTime, closingTime, t) => {
//   if (!openingTime || !closingTime) return null;
//
//   const now = new Date();
//   const opening = new Date(openingTime);
//   const closing = new Date(closingTime);
//
//   const minutesUntilClosing = Math.floor((closing - now) / (1000 * 60));
//
//   if (now >= opening && now <= closing) {
//     if (minutesUntilClosing <= 20) {
//       return { status: 'closing-soon', text: t('restaurants.closingSoon') };
//     }
//     return { status: 'open', text: t('restaurants.open') };
//   }
//   return { status: 'closed', text: t('restaurants.closed') };
// };

// eslint-disable-next-line no-unused-vars
const RestaurantItem = ({ restaurant, t, i18n: _i18n, onLocationClick, sortBy }) => {
  const locationData = restaurant.location ? {
    ...restaurant,
    location: {
      latitude: restaurant.location.latitude,
      longitude: restaurant.location.longitude
    }
  } : null;

  // Extraire le temps d'attente du restaurant
  const waitTime = restaurant.queue?.STANDBY?.waitTime;
  
  // Extraire les disponibilités par taille de groupe
  const diningAvailability = restaurant.diningAvailability || [];
  
  // Trier les disponibilités par taille de groupe
  const sortedAvailability = [...diningAvailability].sort((a, b) => a.partySize - b.partySize);

  return (
    <div className="restaurant-item">
      <div className="restaurant-name">
        {restaurant.name}
        {restaurant.location && (
          <button
            className="icon-button"
            onClick={() => onLocationClick(locationData)}
            aria-label={t('common.viewLocation')}
          >
            <MapPin size={16} />
          </button>
        )}
      </div>
      {restaurant.status && (
        <div className="restaurant-status">
          <span className={`status-pill ${restaurant.status.toLowerCase()}`}>
            {restaurant.status === 'OPERATING' ? t('restaurants.open') : t('restaurants.closed')}
          </span>
        </div>
      )}
      {sortBy === 'alphabetical' && restaurant.parkName && (
        <div className="restaurant-park">{restaurant.parkName}</div>
      )}
      
      {/* Afficher le temps d'attente s'il est disponible */}
      {waitTime !== undefined && waitTime !== null && (
        <div className="restaurant-wait-time">
          <span className="wait-time-label">{t('restaurants.waitTime')}:</span>
          <span className="wait-time-value">{waitTime} {t('min')}</span>
        </div>
      )}
      
      {/* Afficher les disponibilités par taille de groupe si disponibles */}
      {sortedAvailability.length > 0 && (
        <div className="restaurant-availability">
          <div className="availability-title">{t('restaurants.availability')}:</div>
          <div className="availability-list">
            {sortedAvailability.map((availability, index) => (
              <div key={index} className="availability-item">
                <span className="party-size">{availability.partySize} {t('restaurants.persons')}</span>
                <span className="wait-time">{availability.waitTime} {t('min')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Unused component - reserved for future implementation
// const ServiceItem = ({ service, t, onLocationClick }) => {
//   const locationData = service.location ? {
//     ...service,
//     location: {
//       latitude: service.location.latitude,
//       longitude: service.location.longitude
//     }
//   } : null;

//   return (
//     <div className="service-item">
//       <div className="service-name">
//         {service.name}
//         {service.location && (
//           <button
//             className="icon-button"
//             onClick={() => onLocationClick(locationData)}
//             aria-label={t('common.viewLocation')}
//           >
//             <MapPin size={16} />
//           </button>
//         )}
//       </div>
//       {service.status && (
//         <div className="service-status">
//           <span className={`status-pill ${service.status.toLowerCase()}`}>
//             {service.status === 'OPERATING' ? t('restaurants.open') : t('restaurants.closed')}
//           </span>
//         </div>
//       )}
//     </div>
//   );
// };

const DisneylandResortRestaurants = ({ updateLoadingState }) => {
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [restaurantsData, setRestaurantsData] = useState(null);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();
  const [sortBy, setSortBy] = useState('alphabetical');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [contentType, setContentType] = useState('restaurants');
  // Supprimé servicesDropdownOpen non utilisé
  // const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(DISNEY_CALIFORNIA_ADVENTURE_WAIT_TIMES_API);
        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }
        const result = await response.json();
        setRestaurantsData(result);
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des données:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!loading && restaurantsData) {
      const timer = setTimeout(() => {
        updateLoadingState(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      updateLoadingState(true);
    }
  }, [loading, restaurantsData, updateLoadingState]);
  
  // Gérer l'état de chargement local pour le skeleton
  useEffect(() => {
    if (loading) {
      setIsLoading(true);
    } else {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const normalizeName = (name) => {
    if (!name) return '';
    return name.toLowerCase()
      .replace(/[®™]/g, '')
      .replace(/'n'/g, 'n')
      .replace(/[']/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
    
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getUserLocation = () => {
    setLocationError(null);
    
    const handleSuccess = (position) => {
      const { latitude, longitude } = position.coords;
      setUserCoords({ lat: latitude, lng: longitude });
      
      // Forcer un tri par proximité
      setSortBy('proximity');
    };
    
    const handleError = (error) => {
      let errorMessage = '';
      
      switch(error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = t('geolocation.permissionDenied');
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = t('geolocation.positionUnavailable');
          break;
        case error.TIMEOUT:
          errorMessage = t('geolocation.timeout');
          break;
        default:
          errorMessage = t('geolocation.unknownError');
      }
      
      setLocationError(errorMessage);
      
      // Revenir au tri alphabétique en cas d'erreur
      setSortBy('alphabetical');
    };
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      });
    } else {
      setLocationError(t('geolocation.notSupported'));
    }
  };

  // Filtrer les restaurants de tous les parcs de Disneyland Resort
  const allRestaurants = useMemo(() => {
    if (!restaurantsData?.waitTimes?.liveData) return [];
    
    return restaurantsData.waitTimes.liveData.filter(item => 
      item.entityType === 'RESTAURANT'
    ).map(restaurant => {
      // Déterminer le parc en fonction du parkId
      let parkName = '';
      if (restaurant.parkId === '7340550b-c14d-4def-80bb-acdb51d49a66') {
        parkName = 'Disneyland Park';
      } else if (restaurant.parkId === '832fcd51-ea19-4e77-85c7-75d5843b127c') {
        parkName = 'Disney California Adventure';
      } else {
        parkName = 'Disneyland Resort';
      }
      
      return {
        ...restaurant,
        parkName: parkName,
        isService: false
      };
    });
  }, [restaurantsData]);

  // Trier les restaurants en fonction du critère sélectionné
  const sortedRestaurants = useMemo(() => {
    if (!allRestaurants.length) return [];
    
    let sorted = [...allRestaurants];
    
    switch(sortBy) {
      case 'proximity':
        if (userCoords) {
          sorted.sort((a, b) => {
            const distA = calculateDistance(
              userCoords.lat, userCoords.lng, 
              a.location?.latitude, a.location?.longitude
            );
            const distB = calculateDistance(
              userCoords.lat, userCoords.lng, 
              b.location?.latitude, b.location?.longitude
            );
            return distA - distB;
          });
        }
        break;
      case 'emplacement':
        // Pas de tri supplémentaire car on groupe par emplacement
        break;
      case 'alphabetical':
      default:
        sorted.sort((a, b) => normalizeName(a.name).localeCompare(normalizeName(b.name)));
    }
    
    return sorted;
  }, [allRestaurants, sortBy, userCoords]);

  const mapRestaurants = useMemo(() => {
    if (contentType !== 'restaurants') {
      return [];
    }

    return sortedRestaurants.map((restaurant) => ({
      ...restaurant,
      entityType: 'RESTAURANT'
    }));
  }, [contentType, sortedRestaurants]);

  const { viewMode, setViewMode, hasMappableEntities } = useEntityMapView('parkViewMode_disneyland-resort-restaurants', mapRestaurants);

  // Grouper les restaurants par emplacement (land)
  const restaurantsByLocation = useMemo(() => {
    if (sortBy !== 'emplacement') {
      // Si on ne trie pas par emplacement, retourner un seul groupe
      return { 'all': sortedRestaurants };
    }
    
    const grouped = {};
    
    sortedRestaurants.forEach(restaurant => {
      const land = restaurant.land || t('common.unknown');
      if (!grouped[land]) {
        grouped[land] = [];
      }
      grouped[land].push(restaurant);
    });
    
    // Trier chaque groupe par ordre alphabétique
    Object.keys(grouped).forEach(land => {
      grouped[land].sort((a, b) => normalizeName(a.name).localeCompare(normalizeName(b.name)));
    });
    
    return grouped;
  }, [sortedRestaurants, sortBy, t]);

  if (error) return <div className="error-message">{t('errors.fetchRestaurants')}</div>;

  const getTitleByContentType = (contentType, t) => {
    switch(contentType) {
      case 'restaurants':
        return t('restaurants.title');
      case 'services':
        return t('restaurants.servicesTitle');
      default:
        return t('restaurants.title');
    }
  };

  return (
    <div className="park-content loaded" style={{ opacity: 1 }}>
      {isLoading ? (
          <div className="attractions-loading-container">
            <RestaurantsSkeleton count={8} />
          </div>
        ) : null}
      
      {!isLoading && (
        <>
          <div className="park-header">
            <div className="header-main">
              <div className="header-title">
                <h2 className="park-title">{getTitleByContentType(contentType, t)}</h2>
                <div className="content-type-tabs">
                  <button 
                    className={`content-type-tab ${contentType === 'restaurants' ? 'active' : ''}`}
                    onClick={() => setContentType('restaurants')}
                  >
                    {t('restaurants.restaurants')}
                  </button>
                  <button 
                    className={`content-type-tab ${contentType === 'services' ? 'active' : ''}`}
                    onClick={() => setContentType('services')}
                  >
                    {t('restaurants.services')}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="filter-controls">
              {contentType === 'restaurants' && (
                <>
                  <div className="sort-dropdown-container">
                    <button 
                      className="sort-dropdown-button"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                      {sortBy === 'alphabetical' ? t('sortBy.name') : 
                       sortBy === 'emplacement' ? t('sortBy.land') :
                       sortBy === 'proximity' ? t('sortBy.proximity') :
                       t('sortBy.name')}
                      <span className={`arrow ${dropdownOpen ? 'up' : ''}`}>▼</span>
                    </button>
                    {dropdownOpen && (
                      <div className="sort-dropdown-menu">
                        <button onClick={() => { setSortBy('alphabetical'); setDropdownOpen(false); }}>
                          {t('sortBy.name')}
                        </button>
                        <button onClick={() => { setSortBy('emplacement'); setDropdownOpen(false); }}>
                          {t('sortBy.land')}
                        </button>
                        <button onClick={() => { 
                          getUserLocation();
                          setSortBy('proximity');
                          setDropdownOpen(false);
                        }}>
                          {t('sortBy.proximity')}
                        </button>
                      </div>
                    )}
                  </div>
                  {hasMappableEntities && (
                    <button
                      className="view-mode-toggle"
                      onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
                      title={viewMode === 'list' ? t('viewMode.switchToMap', 'Voir la carte') : t('viewMode.switchToList', 'Voir la liste')}
                    >
                      {viewMode === 'list' ? (
                        <>
                          <MapIcon size={18} />
                          <span>{t('viewMode.map', 'Carte')}</span>
                        </>
                      ) : (
                        <>
                          <List size={18} />
                          <span>{t('viewMode.list', 'Liste')}</span>
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {contentType === 'restaurants' ? (
            viewMode === 'list' ? (
              Object.entries(restaurantsByLocation).map(([location, items]) => (
                <div key={location} className="location-group">
                  {sortBy === 'emplacement' && <h3 className="location-title">{location}</h3>}
                  <div className="restaurants-list">
                    {items.filter(item => !item.isService).map(restaurant => (
                      <RestaurantItem
                        key={restaurant.id}
                        restaurant={restaurant}
                        t={t}
                        i18n={i18n}
                        onLocationClick={setSelectedLocation}
                        sortBy={sortBy}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="park-map-container">
                <ParkInteractiveMap
                  parkId="disneyland-resort"
                  parkSlug="disneyland-california"
                  parkName="Disneyland Resort"
                  attractions={mapRestaurants}
                  mapEntities={mapRestaurants}
                  loading={loading}
                />
              </div>
            )
          ) : (
            <div className="services-message">
              {t('restaurants.noServicesAvailable')}
            </div>
          )}

          {selectedLocation && (
            <LocationModal
              isOpen={!!selectedLocation}
              onClose={() => setSelectedLocation(null)}
              attraction={selectedLocation}
              t={t}
            />
          )}

          {locationError && (
            <div className="error-message geolocation-error">
              {locationError}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DisneylandResortRestaurants;
